// SkuCatalogStore — sku-admin-facing state for /admin/sku-catalog/*.
// Backed by rd's plex_sku_catalog endpoints.
//
// Same pattern as EntitlementsStore: class + makeAutoObservable, async
// actions return promises so callers can chain UI feedback.

import { makeAutoObservable, runInAction } from "mobx";

import {
  fetchAllSkus,
  addSku as apiAdd,
  updateSku as apiUpdate,
  deprecateSku as apiDeprecate,
} from "../queries/skuCatalog";

class SkuCatalogStore {
  // Raw SKUs from the admin endpoint, includes deprecated.
  skus = [];

  // UI state
  loading = false;
  error = null;
  lastFetchedAt = null;

  // Inflight write tracker for per-row spinners. Keyed by sku_key.
  pendingWrites = new Set();

  constructor(rootStore) {
    this.root = rootStore;
    makeAutoObservable(this, { root: false });
  }

  get authData() {
    return this.root?.userStore?.authData;
  }

  // ─── Derived ─────────────────────────────────────────────────────────

  get activeSkus() {
    return this.skus.filter(s => s.status === "active");
  }

  get deprecatedSkus() {
    return this.skus.filter(s => s.status === "deprecated");
  }

  isPending(skuKey) {
    return this.pendingWrites.has(skuKey);
  }

  // ─── Actions ─────────────────────────────────────────────────────────

  async load() {
    this.loading = true;
    this.error = null;
    try {
      const resp = await fetchAllSkus(this.authData, { includeDeprecated: true });
      const data = await resp.json();
      runInAction(() => {
        // Server returns {status, content: {skus}} — unwrap.
        this.skus = data?.content?.skus || [];
        this.lastFetchedAt = new Date().toISOString();
      });
    } catch (e) {
      runInAction(() => { this.error = e?.message || String(e); });
    } finally {
      runInAction(() => { this.loading = false; });
    }
  }

  async add({ sku_key, display_name, description, status = "active" }) {
    this.pendingWrites.add(sku_key);
    try {
      const resp = await apiAdd(this.authData, { sku_key, display_name, description, status });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      await this.load();
    } finally {
      runInAction(() => { this.pendingWrites.delete(sku_key); });
    }
  }

  async update(skuKey, fields) {
    this.pendingWrites.add(skuKey);
    try {
      const resp = await apiUpdate(this.authData, skuKey, fields);
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      await this.load();
    } finally {
      runInAction(() => { this.pendingWrites.delete(skuKey); });
    }
  }

  async deprecate(skuKey) {
    this.pendingWrites.add(skuKey);
    try {
      const resp = await apiDeprecate(this.authData, skuKey);
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      await this.load();
    } finally {
      runInAction(() => { this.pendingWrites.delete(skuKey); });
    }
  }
}

export default SkuCatalogStore;
